import { Tab, Tabs } from '@mui/material';
import { useState } from 'react';
import { AdminSettingsFormWrapper } from 'components/shared/AdminSettings/AdminSettingsFormWrapper';
import * as yup from 'yup';
import { FormContent } from './components/FormContent';
import { useGetAdminSettings } from '../../../../queries';
import { AdminSettingsResponse } from '../../../types';
import {
  transformAdminSettingsToFormValues,
  transformAdminSettingsToRequestValues
} from '../../../utils';
import { useSaveResource } from 'RestAPIs/useSaveResource';
import { ADMIN_SETTINGS_GROUP } from 'RestAPIs/endpoints';
import { useTranslation } from 'react-i18next';

export const MAIL_GROUP = 'mail';
export const SMS_GROUP = 'sms';

export const MailAndSMS = () => {
  const { t } = useTranslation('common');
  const [value, setValue] = useState(0);

  const { data: sms = [], isFetching: isFetchingSMS } =
    useGetAdminSettings<AdminSettingsResponse>(SMS_GROUP);
  const { data: mail = [], isFetching: isFetchingMail } =
    useGetAdminSettings<AdminSettingsResponse>(MAIL_GROUP);

  const { isLoading: isMailSaving, mutate: saveMail } = useSaveResource({
    url: ADMIN_SETTINGS_GROUP,
    id: mail[0]?.groupId,
    invalidateQueryKey: [SMS_GROUP]
  });

  const { isLoading: isSMSSaving, mutate: saveSMS } = useSaveResource({
    url: ADMIN_SETTINGS_GROUP,
    id: sms[0]?.groupId,
    invalidateQueryKey: [MAIL_GROUP]
  });

  const mailSettings = mail[0];
  const smsSettings = sms[0];

  const schema = yup.object({
    mail: yup.object({
      active: yup.boolean(),
      valid: yup.boolean(),
      mail_smtp_host: yup.string(),
      mail_smtp_sender_address: yup.string().email(),
      mail_smtp_conn_type: yup.string(),
      mail_smtp_username: yup.string(),
      mail_smtp_password: yup.string(),
      mail_smtp_port: yup.number(),
      mail_smtp_timeout: yup.number(),
      mail_smtp_mime_charset: yup.string()
    }),
    sms: yup.object({
      active: yup.boolean(),
      valid: yup.boolean(),
      sms_application_name: yup.string(),
      sms_max_length: yup.string(),
      sms_sender_address: yup.string().email()
    })
  });

  const onSubmit = async ({ mail: mailFormValues, sms: smsFormValues }) => {
    await Promise.all([
      saveMail({
        ...mailSettings,
        settings: transformAdminSettingsToRequestValues(
          mailFormValues,
          mailSettings?.settings
        ),
        active: mailFormValues.active
      }),
      saveSMS({
        ...smsSettings,
        settings: transformAdminSettingsToRequestValues(
          smsFormValues,
          smsSettings?.settings
        ),
        active: smsFormValues.active
      })
    ]);
  };

  return (
    <AdminSettingsFormWrapper
      submitForm={onSubmit}
      schema={schema}
      fixedHeightForm={false}
      isLoading={isMailSaving || isSMSSaving}
      data={{
        mail: {
          ...transformAdminSettingsToFormValues(mailSettings),
          active: mailSettings?.active,
          valid: mailSettings?.valid
        },
        sms: {
          ...transformAdminSettingsToFormValues(smsSettings),
          active: smsSettings?.active,
          valid: smsSettings?.valid
        }
      }}
      isFetching={isFetchingSMS || isFetchingMail}
    >
      <Tabs value={value} onChange={(_, value) => setValue(value)}>
        <Tab label={t('mailAndSMS.mailSection.name')} value={0} />
        <Tab label={t('mailAndSMS.smsSection.name')} value={1} />
      </Tabs>
      <FormContent selectedTab={value} />
    </AdminSettingsFormWrapper>
  );
};
